import { dayjs } from "@/core/utils/date";
import { formatPhoneNumber } from "@/core/utils/phone";
import {
  isPayrollStatementDeliveryFailed,
  isPayrollStatementDeliveryInProgress,
  isSmsDeliveryWithCarrier,
  PAYROLL_STATEMENT_DELIVERY_STATUS,
} from "./delivery-status-rules";
import type { PollingResult } from "./polling";
import type {
  PayrollStatementNotificationDelivery,
  PayrollStatementNotificationDeliveryStatus,
  PayrollStatementNotificationRecipient,
  PayrollStatementNotificationState,
  PayrollStatementPackApproval,
  PayrollStatementPackApprovalsResponse,
  PayrollStatementProjectionBuild,
  PayrollStatementSentPack,
  PayrollStatementSentPackState,
  PayrollStatementSmsDelivery,
  PayrollStatementSmsDeliveryStatus,
  SubmittedPayrollStatementNote,
} from "./types";

const REASON_LABELS: Record<string, string> = {
  pay_period_not_found: "Pay period not found",
  completed_statement_run_not_found: "No completed PayPack run",
  missing_email: "Missing saved email",
  missing_phone: "No phone number to text",
  sms_opted_out: "Opted out of texts",
  owner_preview_only: "Owners preview only",
  statement_not_available: "PayPack preview unavailable",
};

const STATUS_LABELS: Record<
  PayrollStatementNotificationDeliveryStatus,
  string
> = {
  pending: "Pending",
  enqueued: "Queued",
  succeeded: "Sent",
  failed: "Failed",
  skipped: "Skipped",
};

const SMS_STATUS_LABELS: Record<PayrollStatementSmsDeliveryStatus, string> = {
  pending: "Text pending",
  enqueued: "Texting",
  succeeded: "Texted",
  failed: "Text failed",
  skipped: "Not texted",
};

const getDeliveryId = (
  delivery: PayrollStatementNotificationDelivery | null | undefined,
) => delivery?.id ?? delivery?.delivery_id;

/**
 * Whether approving is even available to this technician.
 *
 * `pack_released` is the server's own answer, and is what the approval endpoint
 * actually requires — so this asks for it rather than inferring it. Inferring it from
 * `delivery_history` was wrong in a way that reopened the bug this is here to fix: a
 * delivery row is created before the send job runs and can fail before it ever
 * records a release (missing statement, a validation error, an address that changed
 * after queueing), so a technician could hold a delivery row, be unable to approve,
 * and still sit in a total the count could then never reach.
 *
 * `eligible` covers the other half — a pack that has not gone out yet but can.
 */
export const canApprovePack = (
  recipient: PayrollStatementNotificationRecipient,
) => recipient.eligible || !!recipient.pack_released;

/**
 * Folds the polled approvals onto the frozen recipients, so every reader below — the
 * column, the header count, the sidebar — sees one shape and need not know which
 * request the fresher answer came from.
 *
 * The two lists mean different things by absence, and getting that backwards is how a
 * withdrawn tick either sticks around or takes its own explanation with it:
 *
 * - `pack_approvals` absent means NO STANDING APPROVAL. Clearing on absence is the
 *   point — it is what lets a withdrawal made in another browser take the tick off
 *   this one.
 * - `approval_histories` absent means NOTHING TO TELL, so a withdrawal has to stay in
 *   it. And the whole FIELD being absent means an API that does not serve histories
 *   yet, which must leave whatever the page loaded with alone rather than blanking it.
 *
 * And `current: null` from the poll means THE POLL COULD NOT MEASURE, which is not an
 * answer and must not overwrite one — see `keepKnownComparison`.
 */
/**
 * The polled approval, keeping a comparison the page already has when the poll could
 * not make one.
 *
 * The approvals endpoint never BUILDS a statement projection — that is the whole
 * reason it can answer every minute — so it compares against a bundle only if one is
 * warm, and reports `current: null` when none is. The bundle expires an hour after it
 * is built and only the full payload rebuilds it, and that payload is cached for the
 * life of the page. So a manager who leaves the PayPacks table open long enough gets a
 * poll that cannot compare anybody, and every tick on the page would be replaced by a
 * "we cannot tell" that describes the cache rather than their pay.
 *
 * `null` from the poll is the absence of a measurement, and the measurement the page
 * already holds is the best one available, so it stands. Only for the SAME act: a
 * re-approval moves `approved_at` and a rerun moves the run id, and either means the
 * old answer was about something else. Everything the poll can actually answer —
 * `true`, `false`, or the approval being gone — is taken as given.
 *
 * "Already holds" means the LATEST answer, not the one the page loaded with. Every
 * poll is merged against the frozen payload, so without `known` this would restore a
 * tick from page load over a "Pay changed" a poll had since reported — see
 * `useKnownPackApprovals`, which is where that memory lives.
 */
const keepKnownComparison = (
  known: PayrollStatementPackApproval | null | undefined,
  polled: PayrollStatementPackApproval | null,
) => {
  if (!polled || polled.current !== null) return polled;
  if (!known || known.current === null) return polled;
  if (
    known.approved_at !== polled.approved_at ||
    known.ai_script_run_id !== polled.ai_script_run_id
  ) {
    return polled;
  }

  return { ...polled, current: known.current };
};

export const mergePolledPackApprovals = (
  recipients: PayrollStatementNotificationRecipient[],
  polled: PayrollStatementPackApprovalsResponse | undefined,
  known: Map<string, PayrollStatementPackApproval> = new Map(),
): PayrollStatementNotificationRecipient[] => {
  if (!polled) return recipients;

  const approvalByTechnician = new Map(
    polled.pack_approvals.map(({ technician_id, ...approval }) => [
      technician_id,
      approval,
    ]),
  );
  const historyByTechnician = new Map(
    (polled.approval_histories ?? []).map(({ technician_id, history }) => [
      technician_id,
      history,
    ]),
  );
  const released = new Set(polled.released_technician_ids);

  return recipients.map((recipient) => ({
    ...recipient,
    // Release moves during the same sitting — a manager who sends packs and then
    // watches for approvals would otherwise count against the state frozen at load.
    pack_released: released.has(recipient.technician_id),
    pack_approval: keepKnownComparison(
      known.get(recipient.technician_id) ?? recipient.pack_approval,
      approvalByTechnician.get(recipient.technician_id) ?? null,
    ),
    approval_history: polled.approval_histories
      ? (historyByTechnician.get(recipient.technician_id) ?? [])
      : recipient.approval_history,
  }));
};

/**
 * How many technicians have approved their own pay, for the header count.
 *
 * Counted over the technicians who COULD approve, so the figure can reach its own
 * total. Both halves come from that same set: an approval from somebody since made
 * ineligible would otherwise read as "3 of 2".
 *
 * `stale` — approvals of figures that have moved since — are counted OUT of
 * `approved` rather than alongside it. A manager reading "20 of 20 approved" over a
 * workbook that has been edited or rerun since would be reading a number that is not
 * true of the pay in front of them.
 *
 * An approval the API could not compare (`current: null`) is counted apart from both,
 * as `unknown`. It cannot go in `stale`, whose only sentence says the figures changed
 * — that states as fact the thing nobody could work out. It cannot go in `approved`
 * either: this line is read as "N of the people you are about to pay have signed off
 * on these figures", and folding an unverifiable approval into that number buys
 * confidence the comparison never supported (ENG-2511).
 */
export const summarizePackApprovals = (
  recipients: PayrollStatementNotificationRecipient[],
) => {
  const countable = recipients.filter(canApprovePack);
  const approvals = countable
    .map((recipient) => recipient.pack_approval)
    .filter((approval) => !!approval);

  return {
    approved: approvals.filter((approval) => approval.current === true).length,
    stale: approvals.filter((approval) => approval.current === false).length,
    unknown: approvals.filter((approval) => approval.current === null).length,
    total: countable.length,
  };
};

/**
 * The technicians whose sent PayPack no longer describes their pay.
 *
 * The set a resend should go to, and the reason this is a function rather than a
 * filter written at each call site: `changed` is three-valued and `null` means the
 * comparison could not be made. `filter(r => r.sent_pack?.changed !== false)` and
 * `filter(r => !!r.sent_pack?.changed)` look equally reasonable and the first one
 * emails everybody who was ever sent a pack before this feature shipped.
 *
 * Restricted to technicians who can actually be sent to, so the count the banner
 * offers matches the batch the button can send: somebody whose email has since been
 * removed cannot be resent to, and counting them makes the button under-deliver
 * against its own label.
 */
export const getRecipientsWithChangedPacks = (
  recipients: PayrollStatementNotificationRecipient[],
) =>
  recipients.filter(
    (recipient) => recipient.sent_pack?.changed === true && recipient.eligible,
  );

/**
 * Technicians whose sent pack has moved but who can no longer be sent to.
 *
 * Reported separately rather than dropped, because dropping them was a real hole: the
 * row still marks the pack as needing a resend (that mark is about the pack, not about
 * whether we can mail it), so a manager could count three marks against a banner
 * offering two with nothing on the page explaining the third. This is reachable in one
 * step — an edit that moves someone's figures can also break their statement audit,
 * which is what makes them ineligible.
 *
 * They need a different action from the manager (fix the address, fix the pack), so
 * they are named and left out of the batch rather than silently included in it.
 */
export const getBlockedChangedPackRecipients = (
  recipients: PayrollStatementNotificationRecipient[],
) =>
  recipients.filter(
    (recipient) => recipient.sent_pack?.changed === true && !recipient.eligible,
  );

/**
 * Whether the polled send counts disagree with the payload the page is holding.
 *
 * The notifications payload is cached with `staleTime: Infinity` and only the
 * approvals endpoint polls, so a send made in another tab or by another manager is
 * invisible here: the banner carries on offering to resend packs that are already
 * current, and pressing it sends a second, redundant round of email.
 *
 * Compared on `send_count` and `last_sent_at` — the two things that move on a send
 * and that the polled endpoint can report without building projections. Undefined
 * `states` (an older server) reports no drift rather than constant drift.
 */
export const hasSentPackDrift = ({
  recipients,
  states,
}: {
  recipients: PayrollStatementNotificationRecipient[];
  states?: PayrollStatementSentPackState[];
}) => {
  if (!states) return false;

  const held = new Map(
    recipients.map((recipient) => [
      recipient.technician_id,
      recipient.sent_pack,
    ]),
  );

  return states.some((state) => {
    // A technician the payload does not know about at all is not drift — they are
    // outside this page's roster, and refetching would not add them.
    if (!held.has(state.technician_id)) return false;

    const sentPack = held.get(state.technician_id);
    return (
      sentPack?.send_count !== state.send_count ||
      sentPack?.last_sent_at !== state.last_sent_at
    );
  });
};

// Matches MissingTechApprovalWarning, the other banner on this page: enough names to
// orient the reader, then a count. Past this the rows carry it — they are marked.
const NAMED_LIMIT = 6;

const summarizeNames = (
  recipients: PayrollStatementNotificationRecipient[],
) => {
  const names = recipients.map((recipient) => recipient.name);
  if (names.length <= NAMED_LIMIT) return names.join(", ");

  return `${names.slice(0, NAMED_LIMIT).join(", ")} and ${names.length - NAMED_LIMIT} more`;
};

/**
 * The one reason every blocked pack shares, when they share one.
 *
 * Named only when unanimous: "missing saved email" is useful, and stapling one
 * technician's reason onto a list of four who failed for different reasons is worse
 * than saying nothing, because the rows already carry each one.
 */
const sharedBlockedReason = (
  recipients: PayrollStatementNotificationRecipient[],
) => {
  const reasons = recipients.map(
    (recipient) => recipient.ineligible_reason ?? "",
  );
  const first = reasons[0];
  if (!first || reasons.some((reason) => reason !== first)) return null;

  return getNotificationReasonLabel(first)?.toLowerCase() ?? null;
};

/**
 * The banner's two lines, built together so neither can silently omit the other's
 * subject.
 *
 * Built as a pair because building them apart lost people: the headline fell back to
 * a count when nothing was sendable, while the detail only named blocked technicians
 * when something WAS sendable — so a period whose changed packs were all unsendable
 * showed "2 technicians' pay changed" over "Can't be emailed", naming nobody and
 * giving no reason, while their rows sat there marked.
 *
 * `headline` is null when there is nothing to say at all, which is the banner's own
 * signal not to render.
 */
export const getChangedPacksSummary = ({
  changedRecipients,
  blockedRecipients,
}: {
  changedRecipients: PayrollStatementNotificationRecipient[];
  blockedRecipients: PayrollStatementNotificationRecipient[];
}) => {
  const sendable = changedRecipients.length;
  const blocked = blockedRecipients.length;
  // Named in the headline when there is only one, rather than counted there and named
  // underneath — that spent two lines carrying one fact.
  const named = sendable > 0 ? changedRecipients : blockedRecipients;
  if (named.length === 0) return { headline: null, detail: null };

  const headline =
    named.length === 1
      ? `${named[0].name}'s pay changed after their PayPack was published`
      : `${named.length} technicians' pay changed after their PayPacks were published`;

  const reason = blocked > 0 ? sharedBlockedReason(blockedRecipients) : null;
  const suffix = reason ? ` — ${reason}.` : ".";
  const blockedClause =
    blocked === 0
      ? null
      : sendable === 0
        ? // The headline already counted them, so this only says who and why.
          `${summarizeNames(blockedRecipients)} can't be emailed${suffix}`
        : `${summarizeNames(blockedRecipients)} ${blocked === 1 ? "also needs" : "also need"} a fresh PayPack, but can't be emailed${suffix}`;

  const detail =
    [
      // The names behind a count, but only when the headline gave a count.
      sendable > 1 ? `${summarizeNames(changedRecipients)}.` : null,
      blockedClause,
    ]
      .filter(Boolean)
      .join(" ") || null;

  return { headline, detail };
};

/**
 * The row's own label for a pack that has moved since it went out.
 *
 * Deliberately NOT "Pay changed", which is already what the Tech approved column
 * says when a sign-off is stale. Both sit on the same row in the same gold, and two
 * identical labels meaning two different things — their approval is out of date
 * against the pack we emailed is out of date — is a support question waiting to
 * happen. This names what the manager does about it instead, which is also what the
 * banner offers.
 *
 * The amount/details distinction is carried beside the label rather than inside it
 * ("was $1,200.00" / "same total"), so the label itself stays one short phrase at
 * every width.
 */
export const getSentPackChangeLabel = (
  sentPack?: PayrollStatementSentPack | null,
) => {
  if (sentPack?.changed !== true) return null;

  return "Needs renotify";
};

export const getNotificationReasonLabel = (reason?: string | null) =>
  reason ? (REASON_LABELS[reason] ?? reason.replaceAll("_", " ")) : null;

export const getDeliveryStatusLabel = (
  status?: PayrollStatementNotificationDeliveryStatus | null,
) => (status ? STATUS_LABELS[status] : "Not sent");

export const getSmsDeliveryStatusLabel = (sms: PayrollStatementSmsDelivery) =>
  isSmsDeliveryWithCarrier(sms)
    ? "Sent to carrier"
    : SMS_STATUS_LABELS[sms.status];

/**
 * Why a text did not reach this technician, in words a manager can act on: the
 * skip reason for a text we chose not to send, the error for one that failed.
 */
export const getSmsDeliveryReason = (sms: PayrollStatementSmsDelivery) => {
  if (sms.status === "skipped")
    return getNotificationReasonLabel(sms.skip_reason);
  if (sms.status === "failed") return sms.error;
  return null;
};

/**
 * A number to text, as a manager reads it. formatPhoneNumber assumes a US number
 * and keeps only the last 10 digits, so anything else is shown as stored.
 */
export const formatTextNumber = (phoneNumber: string) =>
  phoneNumber.startsWith("+1")
    ? (formatPhoneNumber(phoneNumber) ?? phoneNumber)
    : phoneNumber;

/** How many of these technicians will also get a text. */
export const getTextableRecipientsCount = (
  recipients: PayrollStatementNotificationRecipient[],
) => recipients.filter(isTextable).length;

const isTextable = (recipient: PayrollStatementNotificationRecipient) =>
  !!recipient.phone_number && !recipient.sms_ineligible_reason;

/**
 * Who a text-only send still reaches another way, and who it doesn't reach at
 * all. Technicians who still need to set up their account are emailed anyway.
 */
export const getTextOnlyReach = (
  recipients: PayrollStatementNotificationRecipient[],
) => ({
  setupCount: recipients.filter((recipient) => recipient.needs_account_setup)
    .length,
  unreachedCount: recipients.filter(
    (recipient) => !isTextable(recipient) && !recipient.needs_account_setup,
  ).length,
});

export const getFailedPayrollStatementDeliveries = (
  deliveries: PayrollStatementNotificationDelivery[],
) =>
  deliveries.filter((delivery) =>
    isPayrollStatementDeliveryFailed(delivery.status),
  );

export const mergePayrollStatementDeliveryStatuses = (
  state: PayrollStatementNotificationState | undefined,
  deliveries: PayrollStatementNotificationDelivery[],
) => {
  if (!state || deliveries.length === 0) return state;

  const byTechnicianId = new Map(
    deliveries.flatMap((delivery) =>
      delivery.technician_id
        ? [[delivery.technician_id, delivery] as const]
        : [],
    ),
  );

  return {
    ...state,
    recipients: state.recipients.map((recipient) => {
      const update = byTechnicianId.get(recipient.technician_id);
      if (!update) return recipient;

      const updateId = getDeliveryId(update);
      const existing = updateId
        ? recipient.delivery_history.find(
            (delivery) => getDeliveryId(delivery) === updateId,
          )
        : undefined;
      const merged = {
        ...existing,
        ...update,
        ...(updateId ? { id: updateId } : {}),
      };
      const deliveryHistory = existing
        ? recipient.delivery_history.map((delivery) =>
            getDeliveryId(delivery) === updateId ? merged : delivery,
          )
        : [{ ...merged, note: recipient.note }, ...recipient.delivery_history];

      return {
        ...recipient,
        latest_delivery: merged,
        delivery_history: deliveryHistory,
      };
    }),
  };
};

export const getDisplayedProjectionBuild = ({
  indexBuild,
  polledBuild,
}: {
  indexBuild: PayrollStatementProjectionBuild | null | undefined;
  polledBuild:
    | PollingResult<PayrollStatementProjectionBuild>
    | null
    | undefined;
}): PollingResult<PayrollStatementProjectionBuild> | null => {
  if (
    polledBuild?.status === "ready" &&
    indexBuild?.status !== "ready" &&
    indexBuild
  ) {
    return indexBuild;
  }

  return polledBuild ?? indexBuild ?? null;
};

export const getDeliveryTimestamp = (
  delivery: PayrollStatementNotificationDelivery | null,
) => delivery?.sent_at ?? delivery?.created_at ?? null;

export const formatNotificationTimestamp = (timestamp?: string | null) =>
  timestamp ? dayjs(timestamp).format("MMM D, YYYY h:mm A") : null;

export const formatNotificationPeriod = ({
  startDate,
  endDate,
  fallback,
}: {
  startDate?: string | null;
  endDate?: string | null;
  fallback: string;
}) =>
  startDate && endDate
    ? `${dayjs(startDate).format("MMM D")} - ${dayjs(endDate).format(
        "MMM D, YYYY",
      )}`
    : fallback;

/**
 * Whether this send only RE-notifies: every technician in it already has a published
 * PayPack for the period. Their statement already shows the current figures — a
 * published period is read live — so the email is a nudge, not a publish, and the
 * copy says so.
 */
export const isRenotifyOnly = (
  recipients: Pick<PayrollStatementNotificationRecipient, "sent_pack">[],
) => recipients.length > 0 && recipients.every((r) => !!r.sent_pack);

export const getSelectedEligibleRecipients = (
  recipients: PayrollStatementNotificationRecipient[],
  selectedRecipientIds: Set<string>,
) =>
  recipients.filter(
    (recipient) =>
      selectedRecipientIds.has(recipient.technician_id) && recipient.eligible,
  );

export const getDefaultSelectedRecipientIds = (
  recipients: PayrollStatementNotificationRecipient[],
) =>
  new Set(
    recipients
      .filter((recipient) => recipient.eligible)
      .map((recipient) => recipient.technician_id),
  );

const areSetsEqual = <T>(first: Set<T>, second: Set<T>) =>
  first.size === second.size &&
  Array.from(first).every((value) => second.has(value));

export const reconcileSelectedRecipientIds = ({
  recipients,
  selectedRecipientIds,
  useDefaultSelection,
}: {
  recipients: PayrollStatementNotificationRecipient[];
  selectedRecipientIds: Set<string>;
  useDefaultSelection: boolean;
}) => {
  const eligibleRecipientIds = getDefaultSelectedRecipientIds(recipients);
  if (useDefaultSelection) return eligibleRecipientIds;

  const nextSelectedRecipientIds = new Set(
    Array.from(selectedRecipientIds).filter((id) =>
      eligibleRecipientIds.has(id),
    ),
  );

  return areSetsEqual(selectedRecipientIds, nextSelectedRecipientIds)
    ? selectedRecipientIds
    : nextSelectedRecipientIds;
};

export const getSendDisabledReason = ({
  canSend,
  cannotSendReason,
  selectedEligibleCount,
}: {
  canSend: boolean;
  cannotSendReason?: string | null;
  selectedEligibleCount: number;
}) => {
  if (!canSend) {
    return (
      getNotificationReasonLabel(cannotSendReason) ??
      "This period can't be published yet."
    );
  }
  if (selectedEligibleCount === 0)
    return "Select at least one eligible technician";
  return null;
};

export const reconcileSubmittedNoteDrafts = ({
  noteDrafts,
  submittedNotes,
  recipients,
}: {
  noteDrafts: Record<string, string>;
  submittedNotes: Record<string, SubmittedPayrollStatementNote>;
  recipients: PayrollStatementNotificationRecipient[];
}) => {
  let nextNoteDrafts = noteDrafts;
  let nextSubmittedNotes = submittedNotes;

  Object.entries(submittedNotes).forEach(([technicianId, submittedNote]) => {
    const recipient = recipients.find(
      ({ technician_id }) => technician_id === technicianId,
    );
    const latestDelivery = recipient?.latest_delivery;
    const latestDeliveryId = getDeliveryId(latestDelivery);

    if (
      !latestDelivery ||
      latestDeliveryId !== submittedNote.deliveryId ||
      isPayrollStatementDeliveryInProgress(latestDelivery.status)
    ) {
      return;
    }

    nextSubmittedNotes = { ...nextSubmittedNotes };
    delete nextSubmittedNotes[technicianId];

    if (
      latestDelivery.status === PAYROLL_STATEMENT_DELIVERY_STATUS.succeeded &&
      noteDrafts[technicianId] === submittedNote.note
    ) {
      nextNoteDrafts = { ...nextNoteDrafts };
      delete nextNoteDrafts[technicianId];
    }
  });

  return { noteDrafts: nextNoteDrafts, submittedNotes: nextSubmittedNotes };
};

export const trackSubmittedNoteDrafts = ({
  submittedNotes,
  deliveries,
  noteDrafts,
}: {
  submittedNotes: Record<string, SubmittedPayrollStatementNote>;
  deliveries: PayrollStatementNotificationDelivery[];
  noteDrafts: Record<string, string>;
}) => {
  const next = { ...submittedNotes };

  deliveries.forEach((delivery) => {
    const technicianId = delivery.technician_id;
    const deliveryId = getDeliveryId(delivery);
    const note = technicianId ? noteDrafts[technicianId] : undefined;

    if (
      delivery.status === PAYROLL_STATEMENT_DELIVERY_STATUS.enqueued &&
      technicianId &&
      deliveryId &&
      note !== undefined
    ) {
      next[technicianId] = { deliveryId, note };
    }
  });

  return next;
};

/**
 * Show usable PayPacks first, then open PackChat threads, then names.
 * The page calls this only after the shared statement build is ready, so the
 * user sees the final order when the recipient table first appears.
 */
export const sortPayrollStatementRecipients = (
  recipients: PayrollStatementNotificationRecipient[],
  awaitingReplyTechnicianIds?: Set<string>,
): PayrollStatementNotificationRecipient[] =>
  [...recipients].sort((first, second) => {
    const availabilityOrder =
      Number(second.statement_preview_available === true) -
      Number(first.statement_preview_available === true);
    if (availabilityOrder !== 0) return availabilityOrder;

    const awaitingReplyOrder =
      Number(awaitingReplyTechnicianIds?.has(second.technician_id) === true) -
      Number(awaitingReplyTechnicianIds?.has(first.technician_id) === true);
    if (awaitingReplyOrder !== 0) return awaitingReplyOrder;

    return first.name.localeCompare(second.name, undefined, {
      sensitivity: "base",
    });
  });
